import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from 'react-to-print';
import './printstyles.css'

import FormDrawer from "components/__new/common/form-drawer";
import PageContent from "../common/PageContent";
import Maybe from "../common/Maybe";
import useLoginInfo from 'hooks/useLoginInfo';

import { Bar, Doughnut } from 'react-chartjs-2';
import { ReactComponent as IconArrowDownLeft } from "assets/images/icons/arrow-down-left.svg";
import { ReactComponent as DownloadIcon } from "assets/images/icons/download-4.svg";
import { ButtonLoader } from "../common/form";
import NumberFormat, { NumberChartFormat } from "utils/numberFormat";
import { MonthName, MonthIndex, monthNames } from "utils/dateFormat";
import CurrencyFormat, { CurrencyFormatKoboOnly } from "utils/currencyFormat";
import * as withdrawalsAction from "store/entities/withdrawals/action";
import logo from "assets/images/icons/logo-color2@svg.svg";


const WithdrawalReport = (props) => {
    const {isOpen, setIsOpen} = props;
    const dispatch = useDispatch();
    const pdfReportRef = useRef();
    const {userInfo} = useLoginInfo();
    const { withdrawalReportLoading, withdrawalReport } = useSelector((state) => state.entities.withdrawals);
    
    const [month, setMonth] = useState(null);
    const [monthReport, setMonthReport] = useState({});
    const [barChartData, setBarChartData] = useState({
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
          {
            label: 'Withdrawal',
            data: [0,0,0,0,0,0,0,0,0,0,0,0],
            backgroundColor: '#e5e7ea',
            borderColor: '#e5e7ea',
            borderRadius: Number.MAX_VALUE,
            barThickness: 20,
            maxBarThickness: 30,
            barPercentage: 0.5,
          },
        ],
    })
    
    const [doughnutChartData, setDoughnutChartData] = useState({
        labels: [
            'Approved Withdrawals',
            'Pending Withdrawals',
            'Rejected Withdrawals',
        ],
        datasets: [{
            label: 'Users',
            data: [70, 20, 10],
            backgroundColor: [
                '#027A48',
                '#F49D1B',
                '#EF4444',
            ],
            hoverOffset: 5
        }]
    })
    
    
    
    const barChartOptions = {
        maintainAspectRatio: false,
        scales: {
            x: {
                offset: true,
                grid:{
                    display: false,
                    drawBorder: false,
                },
            },
            y: {
                beginAtZero: true,
                grace: "5%",
                grid:{
                    display: false,
                    drawBorder: false,
                },
            },
        },
        plugins: {
            datalabels: {
                display: true,
                color: "black",
                backgroundColor: "white",
                padding: {
                    left: 5,
                    right: 5,
                },
                anchor: "end",
                offset: -15,
                align: "start",
                formatter: (value, context) => {
                    return NumberChartFormat(value, "");
                },
            },
            legend: {
                display: false
            }
        },
        onClick: (event, elements) => {
            const selectedMonth = monthNames[elements[0]?.index] ?? null;
            setMonth(selectedMonth);
        },
        onHover: (event, chartElement) => {
            event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
        }
    };
    
    const doughnutChartOptions = {
        rotation: 180,
        borderRadius: 8,
        spacing: 2,
        plugins: {
            legend: false,
            tooltip: {
                callbacks: {
                    label: function(context){
                        // var label = context.label,
                        var currentValue = context.raw,
                            total = context.chart._metasets[context.datasetIndex].total;
                        var percentage = parseFloat((currentValue/total*100)?.toFixed(1));
                        // return label + ": " +currentValue + ' (' + percentage + '%)';
                        return percentage + "%";
                    }
                }
            }
        },
        layout: {
            padding: 5
        },
    };
        
    const getWithdrawalReport = () => {
        dispatch(withdrawalsAction.getWithdrawalReport());
    }
    
    const getCurrentMonth = () => {
        const date = new Date();
        const currentMonthIndex = (date.getMonth() + 1);
        const month = MonthName(currentMonthIndex);
        setMonth(month);
    }
    
    const updateMonthReport = () => {
        const currentMonthReport = withdrawalReport?.data?.filter((report) => report.month === month)[0] ?? {};
        setMonthReport(currentMonthReport);
        
        let updatedDoughnutChartDataset = [
            currentMonthReport?.totalApprovedCount ?? 0,
            currentMonthReport?.totalPendingCount ?? 0,
            currentMonthReport?.totalUnapprovedCount ?? 0,
        ];
        const updatedDoughnutChartData = {
            ...doughnutChartData,
            datasets: [{
                ...doughnutChartData.datasets[0],
                data: updatedDoughnutChartDataset,
            }]
        };
        setDoughnutChartData(updatedDoughnutChartData);
    }
    
    const updateCharts = () => {
        let updatedBarChartDataset = [0,0,0,0,0,0,0,0,0,0,0,0];
        let updatedBarChartColors = [...Array(12)].fill("#E5E7EA");
        withdrawalReport?.data?.map((report) => {
            updatedBarChartDataset[MonthIndex(report.month) - 1] = report.totalAmount;
            if (report.month === month) {updatedBarChartColors[MonthIndex(report.month) - 1] = "#00535C"}
            return null;
        });
        const updatedBarChartData = {
            ...barChartData,
            datasets: [{
                ...barChartData.datasets[0],
                data: updatedBarChartDataset,
                backgroundColor: updatedBarChartColors,
            }]
        };
        setBarChartData(updatedBarChartData);
    }
    
    const downloadAsPdf = useReactToPrint({
        content: () => pdfReportRef.current,
    });
    
    
    
    useEffect(() => {
        getCurrentMonth();
        getWithdrawalReport();
        // eslint-disable-next-line
    }, [])
    
    useEffect(() => {
        if (withdrawalReport?.success === true){
            updateCharts();
            updateMonthReport();
        }
        // eslint-disable-next-line
    }, [withdrawalReport, month])
    
    // const printStyle = (
    //     <style>
    //             {`
    //                 @media print {
    //                     .print-header, .print-footer {
    //                         display: block;
    //                     }
    //                 }
    //                 .print-header, .print-footer {
    //                     display: none;
    //                 }
    //                 .print-header {
    //                     position: fixed;
    //                     top: 0;
    //                     width: 100%;
    //                     text-align: center;
    //                     background: #fff;
    //                     padding: 10px 0;
    //                     border-bottom: 1px solid #000;
    //                 }
    //                 .print-footer {
    //                     position: fixed;
    //                     bottom: 0;
    //                     width: 100%;
    //                     text-align: center;
    //                     background: #fff;
    //                     padding: 10px 0;
    //                     border-top: 1px solid #000;
    //                 }
    //                 .print-content {
    //                     margin-top: 60px; /* Adjust based on header height */
    //                     margin-bottom: 60px; /* Adjust based on footer height */
    //                 }
    //             `}
    //             </style>
    // )
    
    return (
        <FormDrawer display="center" size="lg" isOpen={isOpen} setIsOpen={setIsOpen}>
            <div className="p-6 bg-[#F9FAFB] relative rounded-lg">
                <FormDrawer.Title
                    title="Withdrawal Report"
                    description=" "
                    showCloseButton={true}
                    close={setIsOpen}
                />
                
                <PageContent
                    loading={withdrawalReportLoading}
                    data={withdrawalReport}
                    loadingText="Loading report..."
                />
                
                <Maybe condition={withdrawalReportLoading === false}>
                    <div className="h-[36rem] py-6 overflow-auto">
                        <div ref={pdfReportRef} className="print:p-6 print:relative print:h-full">
                            <div className="w-full print:block mb-6 hidden ">
                                <div className="flex items-center">
                                <img src={logo} alt="EarniPay logo" className="h-6 object-contain" />
                                <span className="text-ep-gray-400 px-2">|</span>
                                <h4 className="font-bold text-lg">{userInfo?.employer?.companyName}</h4>
                                </div>
                            </div>
                            <div className="print-content">
                                <div className="h-64 p-4 bg-white rounded-lg border border-gray-10">
                                    <div className="mb-2 text-sm font-medium">
                                        Withdrawals Overview
                                    </div>
                                    <div className="h-48 overflow-hidden">
                                        <Bar className="h-full" data={barChartData} options={barChartOptions} />
                                    </div>
                                </div>
                                
                                <div className="mt-4 p-4 bg-white flex items-center justify-between rounded-lg border border-gray-100">
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 p-1.5 mr-2 flex-shrink-0 rounded-full bg-[#FEF2F2] border-2 border-[#f9e4e3]">
                                            <IconArrowDownLeft className="w-full h-full object-contain" />
                                        </div>
                                        <div>
                                            <div className="text-fade text-xs">
                                                Month
                                            </div>
                                            <div className="text-fade">
                                                {month ?? "-"}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-2xl font-bold flex items-end">
                                        <span>{CurrencyFormat(monthReport?.totalAmount) ?? "-"}</span>
                                        <Maybe condition={monthReport?.totalAmount}>
                                            <span className="text-base">.{CurrencyFormatKoboOnly(monthReport?.totalAmount) ?? "00"}</span>
                                        </Maybe>
                                    </div>
                                </div>
                                
                                <div className="h-40 mt-4 p-4 bg-white flex items-center justify-between rounded-lg border border-gray-100">
                                    <Maybe condition={monthReport?.totalAmount}>
                                        <div className="h-auto rounded-md relative -left-2 overflow-visible">
                                            <Doughnut className="h-16" data={doughnutChartData} options={doughnutChartOptions} />
                                        </div>
                                        <div className="flex flex-col space-y-3 text-sm">
                                            <div className="flex space-x-2 items-center">
                                                <div className="w-2 h-2 bg-[#027A48] rounded-full"></div>
                                                <div>Approved Withdrawals</div>
                                            </div>
                                            <div className="flex space-x-2 items-center">
                                                <div className="w-2 h-2 bg-[#F49D1B] rounded-full"></div>
                                                <div>Pending Withdrawals</div>
                                            </div>
                                            <div className="flex space-x-2 items-center">
                                                <div className="w-2 h-2 bg-[#EF4444] rounded-full"></div>
                                                <div>Rejected Withdrawals</div>
                                            </div>
                                        </div>
                                    </Maybe>
                                    
                                    <Maybe condition={!monthReport?.totalAmount}>
                                        <div className="w-full py-20 text-center text-fade">
                                            There are no withdrawal activities for this period
                                        </div>
                                    </Maybe>
                                </div>
                                
                                <div className="mt-6 text-fade space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div>Withdrawal Count</div>
                                        <div>{NumberFormat(monthReport?.totalCount, "no-kobo") ?? "-"}</div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>Approved Withdrawals</div>
                                        <div>{NumberFormat(monthReport?.totalApprovedCount, "no-kobo") ?? "-"}</div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>Approved Amount</div>
                                        <div>{CurrencyFormat(monthReport?.totalApprovedAmount, true) ?? "-"}</div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>Rejected Withdrawals</div>
                                        <div>{NumberFormat(monthReport?.totalUnapprovedCount, "no-kobo") ?? "-"}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="print-footer hidden print:block print:fixed print:bottom-20 w-full">
                                <div className="w-full py-4 bottom-0 left-0 right-0 border-t border-gray-200"></div>
                                <div className="mx-auto text-center text-ep-gray-400 text-sm">
                                    <p className="text-ep-gray-400">
                                        <span className="text-ep-primary-600 font-medium">Disclaimer: </span>This is an auto generated report.
                                    </p>
                                    <p className="text-ep-gray-400">
                                    For further inquiries, contact <span className="text-ep-primary-600 font-medium">business@earnipay.com</span>
                                    </p>
                                </div>
                            </div>
                            
                        </div>
                        {/* {printStyle} */}
                        {/* <div ref={pdfReportRef} className="print:p-6">
                            
                            <div className="h-64 p-4 bg-white rounded-lg border border-gray-10">
                                <div className="mb-2 text-sm font-medium">
                                    Withdrawals Overview
                                </div>
                                <div className="h-48 overflow-hidden">
                                    <Bar className="h-full" data={barChartData} options={barChartOptions} />
                                </div>
                            </div>
                            
                            <div className="mt-4 p-4 bg-white flex items-center justify-between rounded-lg border border-gray-100">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 p-1.5 mr-2 flex-shrink-0 rounded-full bg-[#FEF2F2] border-2 border-[#f9e4e3]">
                                        <IconArrowDownLeft className="w-full h-full object-contain" />
                                    </div>
                                    <div>
                                        <div className="text-fade text-xs">
                                            Month
                                        </div>
                                        <div className="text-fade">
                                            {month ?? "-"}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-2xl font-bold flex items-end">
                                    <span>{CurrencyFormat(monthReport?.totalAmount) ?? "-"}</span>
                                    <Maybe condition={monthReport?.totalAmount}>
                                        <span className="text-base">.{CurrencyFormatKoboOnly(monthReport?.totalAmount) ?? "00"}</span>
                                    </Maybe>
                                </div>
                            </div>
                            
                            <div className="h-40 mt-4 p-4 bg-white flex items-center justify-between rounded-lg border border-gray-100">
                                <Maybe condition={monthReport?.totalAmount}>
                                    <div className="h-auto rounded-md relative -left-2 overflow-visible">
                                        <Doughnut className="h-16" data={doughnutChartData} options={doughnutChartOptions} />
                                    </div>
                                    <div className="flex flex-col space-y-3 text-sm">
                                        <div className="flex space-x-2 items-center">
                                            <div className="w-2 h-2 bg-[#027A48] rounded-full"></div>
                                            <div>Approved Withdrawals</div>
                                        </div>
                                        <div className="flex space-x-2 items-center">
                                            <div className="w-2 h-2 bg-[#F49D1B] rounded-full"></div>
                                            <div>Pending Withdrawals</div>
                                        </div>
                                        <div className="flex space-x-2 items-center">
                                            <div className="w-2 h-2 bg-[#EF4444] rounded-full"></div>
                                            <div>Rejected Withdrawals</div>
                                        </div>
                                    </div>
                                </Maybe>
                                
                                <Maybe condition={!monthReport?.totalAmount}>
                                    <div className="w-full py-20 text-center text-fade">
                                        There are no withdrawal activities for this period
                                    </div>
                                </Maybe>
                            </div>
                            
                            <div className="mt-6 text-fade space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>Withdrawal Count</div>
                                    <div>{NumberFormat(monthReport?.totalCount, "no-kobo") ?? "-"}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>Approved Withdrawals</div>
                                    <div>{NumberFormat(monthReport?.totalApprovedCount, "no-kobo") ?? "-"}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>Approved Amount</div>
                                    <div>{CurrencyFormat(monthReport?.totalApprovedAmount, true) ?? "-"}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>Rejected Withdrawals</div>
                                    <div>{NumberFormat(monthReport?.totalUnapprovedCount, "no-kobo") ?? "-"}</div>
                                </div>
                            </div>
                            
                        </div> */}
                    </div>
                    
                    <div className="w-full pt-6 bottom-0 left-0 right-0 border-t border-gray-200">
                        <ButtonLoader type="button" loading={false} onClick={downloadAsPdf} className="btn btn-lg btn-block btn-transparent-primary font-medium">
                            Download Report as PDF
                            <DownloadIcon className="ml-2 h-5" />
                        </ButtonLoader>
                    </div>
                </Maybe>
                
            </div>
        </FormDrawer>
    )
}


export default WithdrawalReport;